import React from 'react';
import { clsx } from 'clsx';

interface TagProps {
  children: React.ReactNode;
  color?: 'default' | 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'pink' | 'gray';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'filled' | 'outlined' | 'soft';
  closable?: boolean;
  onClose?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const Tag: React.FC<TagProps> = ({
  children,
  color = 'default',
  size = 'md',
  variant = 'soft',
  closable = false,
  onClose,
  className,
  style
}) => {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  const colorClasses = {
    filled: {
      default: 'bg-gray-600 text-white',
      blue: 'bg-blue-600 text-white',
      green: 'bg-green-600 text-white',
      red: 'bg-red-600 text-white',
      yellow: 'bg-yellow-600 text-white',
      purple: 'bg-purple-600 text-white',
      pink: 'bg-pink-600 text-white',
      gray: 'bg-gray-500 text-white'
    },
    outlined: {
      default: 'border border-gray-300 text-gray-700 bg-white',
      blue: 'border border-blue-300 text-blue-700 bg-white',
      green: 'border border-green-300 text-green-700 bg-white',
      red: 'border border-red-300 text-red-700 bg-white',
      yellow: 'border border-yellow-300 text-yellow-700 bg-white',
      purple: 'border border-purple-300 text-purple-700 bg-white',
      pink: 'border border-pink-300 text-pink-700 bg-white',
      gray: 'border border-gray-300 text-gray-700 bg-white'
    },
    soft: {
      default: 'bg-gray-100 text-gray-800',
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      purple: 'bg-purple-100 text-purple-800',
      pink: 'bg-pink-100 text-pink-800',
      gray: 'bg-gray-100 text-gray-800'
    }
  };

  const baseClasses = 'inline-flex items-center font-medium rounded-full';

  return (
    <span
      className={clsx(
        baseClasses,
        sizeClasses[size],
        !style && colorClasses[variant][color], // Only apply color classes if no custom style
        className
      )}
      style={style}
    >
      <span>{children}</span>
      {closable && (
        <button
          onClick={onClose}
          className="ml-1 inline-flex items-center justify-center rounded-full hover:bg-black hover:bg-opacity-10 transition-colors"
          style={{ width: '1em', height: '1em' }}
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </span>
  );
};

export default Tag;
export type { TagProps }; 